/* eslint-disable no-restricted-properties */
import {shallowMemoize} from '@sail/utils';
import {getCurrencyISOCode} from '@stripe-internal/cldr';
import createGlobalizeWithLocale from 'src/internal/createGlobalizeWithLocale/createGlobalizeWithLocale';
import currencyToDecimalLength from 'src/internal/currency-formatter/currencyToDecimalLength';
import handleGlobalizeError from 'src/internal/errors/handleGlobalizeError/handleGlobalizeError';
import mergeOptions from 'src/internal/mergeOptions';
import resolveFinalNotation from 'src/internal/resolveFinalNotation';

import type {IReports} from '@sail/observability';
import type {CurrencyFormatterOptions} from 'globalize';
import type {
  FormatCurrencyOptions,
  FormatCurrencyOptionsDefaults,
  FormatCurrencyOptionsWithDefaults,
} from 'src/public/formatCurrency/types';
import type {IntlConfig} from 'src/types';

function getCurrencyDigits(currency: string): number {
  return currencyToDecimalLength(currency.toLowerCase());
}

function calculateMinimumFractionDigits(
  currencyDigits: number,
  userOptions: FormatCurrencyOptionsWithDefaults,
): number | undefined {
  const {locale, currency, minimumFractionDigits, maximumFractionDigits} =
    userOptions;

  if (typeof minimumFractionDigits !== 'undefined') {
    return minimumFractionDigits;
  }

  // Peacefully coalesce minimumFractionDigits to zero if maximumFractionDigits is zero
  if (
    typeof maximumFractionDigits !== 'undefined' &&
    maximumFractionDigits === 0
  ) {
    return 0;
  }

  if (currency === 'BTC' || currency === 'XBT') {
    return locale === 'en-US' ? currencyDigits : 2;
  }

  // Since USDC and EURC support 6 decimal digits but Stripe only uses 2 for the time being,
  // This special case will ensure that our display to the user is as accurate as possible
  // While ensuring that this special handling is only in this one place
  if (currency === 'USDC' || currency === 'EURC') {
    return 2;
  }

  if (locale === 'en-US') {
    return currencyDigits;
  }
}

function calculateMaximumFractionDigits(
  currencyDigits: number,
  userOptions: FormatCurrencyOptions,
): number | undefined {
  const {currency, maximumFractionDigits} = userOptions;

  if (userOptions.arbitraryPrecision) {
    return 20;
  }

  if (typeof maximumFractionDigits !== 'undefined') {
    return maximumFractionDigits;
  }

  if (currency === 'BTC' || currency === 'XBT') {
    return currencyDigits;
  }

  // Since USDC supports 6 decimal digits but Stripe only uses 2 for the time being,
  // This special case will ensure that our display to the user is as accurate as possible
  // While ensuring that this special handling is only in this one place
  if (currency === 'USDC' || currency === 'EURC') {
    return 2;
  }
}

const currencyFormatter = shallowMemoize(
  (
    config: IntlConfig,
    userOptions: FormatCurrencyOptionsWithDefaults,
    reports: IReports,
  ) => {
    const {locale, currency} = userOptions;
    const globalize = createGlobalizeWithLocale(
      config,
      locale,
      undefined,
      reports,
    );
    const currencyDigits = getCurrencyDigits(currency);
    const formatterOptions: CurrencyFormatterOptions = {
      useGrouping: userOptions.useGrouping,
      maximumSignificantDigits: userOptions.maximumSignificantDigits,
      minimumSignificantDigits: userOptions.minimumSignificantDigits,
    };

    const maximumFractionDigits = calculateMaximumFractionDigits(
      currencyDigits,
      userOptions,
    );

    const minimumFractionDigits = calculateMinimumFractionDigits(
      currencyDigits,
      userOptions,
    );

    if (typeof minimumFractionDigits !== 'undefined') {
      formatterOptions.minimumFractionDigits = minimumFractionDigits;
    }

    if (typeof maximumFractionDigits !== 'undefined') {
      formatterOptions.maximumFractionDigits = maximumFractionDigits;
    }

    if (userOptions.style === 'symbol-narrow') {
      formatterOptions.style = 'symbol';
      // @ts-expect-error - TS2339 - Property 'symbolForm' does not exist on type 'CurrencyFormatterOptions'.
      formatterOptions.symbolForm = 'narrow';
    } else if (userOptions.style === 'accounting-symbol-narrow') {
      formatterOptions.style = 'accounting';
      // @ts-expect-error - TS2339 - Property 'symbolForm' does not exist on type 'CurrencyFormatterOptions'.
      formatterOptions.symbolForm = 'narrow';
    } else if (userOptions.style === 'no-currency') {
      formatterOptions.style = 'symbol';
    } else {
      formatterOptions.style = userOptions.style;
    }

    if (userOptions.notation === 'compact') {
      formatterOptions.compact = 'short';

      if (
        typeof userOptions.maximumSignificantDigits === 'undefined' &&
        typeof userOptions.minimumSignificantDigits === 'undefined'
      ) {
        formatterOptions.maximumSignificantDigits = 3;
        formatterOptions.minimumSignificantDigits = 1;
      }
    }

    try {
      // @ts-expect-error - TS2551 - Property 'currencyToPartsFormatter' does not exist on type 'Shared'.
      const formatter = globalize.currencyToPartsFormatter(
        getCurrencyISOCode(currency),
        formatterOptions,
      );

      return {
        globalize,
        formatter,
      };
    } catch (error) {
      handleGlobalizeError(error, reports);
    }
  },
);

/**
 * @see https://sail.stripe.me/apis/intl/useIntl/#format-currency
 */
export default function formatCurrency(
  /** The intl config object containing the locale data. When using this API through the `useIntl` hook, this config object is automatically injected. */
  config: IntlConfig,
  reports: IReports,
  /** The amount to be formatted. The value should be in the smallest currency unit, which varies by currency. E.g. `1 USD` becomes `$0.01`, while `1 JPY` becomes `¥1`. */
  value: number,
  /** An object with options to customize the formatting. */
  options: FormatCurrencyOptions,
): string {
  const defaults: FormatCurrencyOptionsDefaults = {
    locale: config.locale,
    useGrouping: true,
    style: 'symbol',
    arbitraryPrecision: false,
    notation: 'standard',
  };

  const resolvedNotation = resolveFinalNotation(
    options.forceCompactAboveValue,
    value,
    options.notation || defaults.notation,
  );

  const mergedOptions: FormatCurrencyOptionsWithDefaults = mergeOptions(
    config,
    defaults,
    reports,
    options,
    {
      currency: options.currency.toUpperCase(),
      notation: resolvedNotation,
    },
  );

  const {formatter} = currencyFormatter(config, mergedOptions, reports);

  const decimalLength = getCurrencyDigits(mergedOptions.currency);
  const amount = !mergedOptions.arbitraryPrecision
    ? Math.round(value) / Math.pow(10, decimalLength)
    : +value;

  if (mergedOptions.style === 'no-currency') {
    return (formatter(amount) as {type: string; value: string}[])
      .filter(({type}) => type !== 'currency')
      .map((part) => part.value)
      .join('')
      .trim();
  }

  return (formatter(amount) as {type: string; value: string}[])
    .map((part) => {
      // non-ISO currency codes are passed as fake currency codes to Globalize,
      // e.g. "USDC" is passed as "ZZA" to Globalize.
      // So we just use the currency code as the currency symbol when the style is 'code'.
      if (part.type === 'currency' && mergedOptions.style === 'code') {
        return mergedOptions.currency;
      }

      return part.value;
    })
    .join('');
}

export type FormatCurrency = typeof formatCurrency;

export type FormatCurrencyDeclarative = (
  amount: Parameters<FormatCurrency>[2],
  options: Parameters<FormatCurrency>[3],
) => ReturnType<FormatCurrency>;
